<script lang="ts">
  /**
   * MinigameOverlay — fullscreen overlay for skill-check minigames.
   *
   * Registers all minigames, starts a random one on mount, and drives a
   * requestAnimationFrame loop for tick + render. Handles keyboard (Space,
   * A/S/D/F, letter keys) and touch/click input for PWA compatibility.
   *
   * Games auto-cycle: complete → 350ms result display → next random game.
   * Score accumulates across all games played in one loading session.
   */
  import { MinigameManager } from "$lib/minigames/manager";
  import type { MinigameResult } from "$lib/minigames/manager";
  import { RuneCatcher } from "$lib/minigames/runeCatcher";
  import { Heartbeat } from "$lib/minigames/heartbeat";
  import { Lockpick } from "$lib/minigames/lockpick";

  interface Props {
    onDismiss: (result: MinigameResult) => void;
    width?: number;
    height?: number;
  }

  let { onDismiss, width = 40, height = 12 }: Props = $props();

  const manager = new MinigameManager();

  // Register base games
  manager.register(new RuneCatcher());
  manager.register(new Heartbeat());
  manager.register(new Lockpick());

  // Dynamically register additional games if available
  registerOptionalGames();

  function registerOptionalGames(): void {
    // Quiz games
    tryRegister(() => import("$lib/minigames/quickMath"));
    tryRegister(() => import("$lib/minigames/patternComplete"));
    tryRegister(() => import("$lib/minigames/countFast"));
    tryRegister(() => import("$lib/minigames/missingNumber"));
    tryRegister(() => import("$lib/minigames/binaryQuiz"));
    tryRegister(() => import("$lib/minigames/oddOneOut"));
    tryRegister(() => import("$lib/minigames/wordUnscramble"));
    tryRegister(() => import("$lib/minigames/stroopColor"));
    // Action games
    tryRegister(() => import("$lib/minigames/reactionFlash"));
    tryRegister(() => import("$lib/minigames/towerStack"));
    tryRegister(() => import("$lib/minigames/memoryFlash"));
    tryRegister(() => import("$lib/minigames/chessFork"));
    tryRegister(() => import("$lib/minigames/sequenceRepeat"));
    tryRegister(() => import("$lib/minigames/reflexDodge"));
    tryRegister(() => import("$lib/minigames/typingSprint"));
  }

  function tryRegister(loader: () => Promise<Record<string, unknown>>): void {
    loader()
      .then((mod) => {
        // Find the first exported class that looks like a Minigame
        for (const value of Object.values(mod)) {
          if (typeof value === "function" && value.prototype?.init) {
            manager.register(new (value as new () => import("$lib/minigames/manager").Minigame)());
            return;
          }
        }
      })
      .catch(() => {
        // Game module not yet built — silently skip
      });
  }

  manager.start();

  let renderedLines = $state<string[]>([]);
  let gameName = $state(manager.currentGameName() || "Loading...");
  let instruction = $state("Press SPACE or TAP");
  let animFrameId: number;
  let lastTime = 0;

  function loop(now: number): void {
    if (!manager.isActive()) return;

    const dt = lastTime === 0 ? 0 : now - lastTime;
    lastTime = now;

    manager.tick(dt);
    renderedLines = manager.render(width, height);

    // Update display name when game cycles
    const name = manager.currentGameName();
    if (name && name !== gameName) {
      gameName = name;
    }

    // Update instruction based on phase
    instruction = manager.phase() === "result" ? "Next game starting..." : "Press SPACE or TAP";

    animFrameId = requestAnimationFrame(loop);
  }

  function handleInput(key?: string): void {
    if (!manager.isActive()) return;
    if (manager.phase() === "result") return;
    manager.input(key);
  }

  function handleTouch(e: TouchEvent): void {
    e.preventDefault();
    handleInput("space");
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (manager.phase() === "result") return;

    const key = e.key.toLowerCase();

    // Space/tap — action games
    if (e.key === " ") {
      e.preventDefault();
      handleInput("space");
      return;
    }

    // A/S/D/F — quiz game option selection
    if (["a", "s", "d", "f"].includes(key)) {
      e.preventDefault();
      handleInput(key);
      return;
    }

    // Letter keys — typing sprint and other letter-based games
    if (key.length === 1 && key >= "a" && key <= "z") {
      handleInput(key);
    }
  }

  /** Dismiss the minigame externally (e.g. when LLM response arrives). */
  export function dismiss(): void {
    if (animFrameId) cancelAnimationFrame(animFrameId);
    const result = manager.dismiss();
    onDismiss(result);
  }

  // Start the rAF loop on mount; clean up on destroy
  $effect(() => {
    animFrameId = requestAnimationFrame(loop);

    return () => {
      if (animFrameId) cancelAnimationFrame(animFrameId);
    };
  });
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="minigame-backdrop"
  onclick={() => handleInput("space")}
  ontouchstart={handleTouch}
  role="button"
  tabindex="0"
  style="touch-action: manipulation;"
>
  <div class="minigame-panel">
    <div class="minigame-title">{gameName}</div>
    <div class="minigame-instruction">{instruction}</div>
    <pre class="minigame-canvas">{renderedLines.join("\n")}</pre>
  </div>
</div>

<style>
  .minigame-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
    touch-action: manipulation;
  }

  .minigame-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .minigame-title {
    font-family: var(--font-mono);
    font-size: 1.25rem;
    color: var(--fg-bright);
    text-transform: uppercase;
    letter-spacing: 0.2em;
  }

  .minigame-instruction {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--fg-dim);
    animation: pulse 1.5s ease-in-out infinite;
  }

  .minigame-canvas {
    font-family: var(--font-mono);
    font-size: 1rem;
    line-height: 1.2;
    color: var(--fg);
    background: var(--bg);
    border: 1px solid var(--fg-dim);
    padding: 0.5rem;
    margin: 0;
    white-space: pre;
    pointer-events: none;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
  }
</style>
